import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import LinkIcon from '@mui/icons-material/Link'
import { Box, Divider, styled, Typography, Collapse } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { FormLabel, ProgressLoaders, Tag, MButton } from 'components'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useEffect, useState } from 'react'
import { isAuthorized, useMobileBreakpoints } from 'utils'
import { LinkAccountDialog } from './LinkAccountDialog'
import { UnlinkAccountDialog } from './UnlinkAccountDialog'
import { isFeatureActive } from 'config'


interface ILinkedAccounts {
  refreshKey?: number
}

const IS_IC_LINKING_ENABLED = isFeatureActive('ic_link')
const IS_IC_UNLINKING_ENABLED = isFeatureActive('ic_unlink')

export const LinkedAccounts = ({ refreshKey }: ILinkedAccounts): JSX.Element => {
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') ?? ''
  const { handleLogOut, getAllLinkedAccounts, account } = useAuth()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [linkedAccounts, setLinkedAccounts] = useState([])
  const isMobile = useMobileBreakpoints()
  const [showLinkDialog, setShowLinkDialog] = useState<boolean>(false)
  const [showUnlinkDialog, setShowUnlinkDialog] = useState<boolean>(false)

  const [isLinkedAccountOpen, setisLinkedAccountOpen] = useState(true)

  const initLinkedAccounts = async (): Promise<void> => {
    setIsLoading(true)
    try {
      const params = {
        email,
        accountId: account?.accountId || ''
      }
      const result = await getAllLinkedAccounts(params)
      setLinkedAccounts(result?.data)
    } catch (e: any) {
      if (!isAuthorized(e)) await handleLogOut()
      setLinkedAccounts([])
    }
    setIsLoading(false)
  }

  useEffect(() => {
    void initLinkedAccounts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, email, refreshKey])

  if (isLoading) {
    return (
      <Box border={`1px solid ${color.secondary.dark[300]}`} p={2}>
        <ProgressLoaders height='100%' />
      </Box>
    )
  }

  const NoLinkedAccounts = (): JSX.Element => {
    return (
      <Box display="flex" flexDirection="column" gap={1} margin={6} alignItems="center">
        <AccountCircleIcon
          sx={{
            color: color.grey[500],
            backgroundColor: color.grey[200],
            fontSize: 26,
            borderRadius: '50%',
            padding: 1
          }}
        />
        <Typography color={color.black} fontWeight={600} fontSize={18}>
          No existing linked accounts
        </Typography>
        <Typography color={color.grey[700]} fontSize={14}>
          There are no linked accounts for this user. If you think this is an
          error please create a Jira Ticket.
        </Typography>
      </Box>
    )
  }

  const handleClick = (): void => {
    setisLinkedAccountOpen(!isLinkedAccountOpen)
  }

  return (
    <Box border={`1px solid ${color.secondary.dark[300]}`}>
      <Box
        display='flex'
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent='space-between'
        p={1}
        bgcolor={color.grey[50]}
        gap={isMobile ? 1 : 0}
        onClick={handleClick}
      >
        <Box display='flex' flexDirection='row' alignItems='center' gap={1} sx={{ cursor: 'pointer' }}>
          <LinkIcon
            sx={{
              color: color.grey[500],
              backgroundColor: color.grey[200],
              fontSize: 26,
              borderRadius: '50%',
              padding: 1
            }}
          />

          <Box
            display='flex'
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems='center'
            gap={1}
          >
            <Typography color={color.black} fontWeight={600} fontSize={16}>
              Linked Accounts
            </Typography>
            <Tag
              type='success'
              message={`${linkedAccounts.filter((la: any) => la.accountId !== account?.accountId).length} linked account(s)`}
            />
          </Box>
        </Box>

        <Box
          display='flex'
          flexDirection={isMobile ? 'column' : 'row'}
          gap={1}
          alignItems="center"
        >
          {IS_IC_LINKING_ENABLED && (
              <MButton
              variant='outlined'
              type='button'
              rounded='true'
              texttransform='none'
              fullWidth={isMobile}
              onClick={() => {
                setShowLinkDialog(!showLinkDialog)
              }}
            >
              Link in Invoice Cloud
            </MButton>
            )}
            {IS_IC_UNLINKING_ENABLED && linkedAccounts.length > 1 && (
              <MButton
                variant='outlined'
                type='button'
                rounded='true'
                texttransform='none'
                fullWidth={isMobile}
                onClick={() => {
                  setShowUnlinkDialog(!showUnlinkDialog)
                }}
              >
                Unlink Account
              </MButton>
            )}

            {isLinkedAccountOpen ? <ExpandLess /> : <ExpandMore />}
        </Box>
      </Box>

      <Divider sx={{ color: color.secondary.dark[300] }} />
      <Collapse in={isLinkedAccountOpen} timeout="auto" unmountOnExit>
        {linkedAccounts.filter((la: any) => la.accountId !== account?.accountId).length === 0
          ? (
              <NoLinkedAccounts />
            )
          : isMobile
          ? (
              <Box>
                {linkedAccounts.filter((la: any) => la.accountId !== account?.accountId).map((la: any, index: number) => (
                  <LinkedAccountsTableMobileView
                    key={index}
                    accountId={la.accountId}
                    serviceAddress={la.serviceAddress}
                  />
                ))}
              </Box>
            )
          : (
            <Table>
              <TableHeader>
                <ItemWrapper width='20%'>
                  <FormLabel textcolor={color.grey[600]}>
                    Account Number
                  </FormLabel>
                </ItemWrapper>
                <ItemWrapper width='80%'>
                  <FormLabel textcolor={color.grey[600]}>
                    Service Address
                  </FormLabel>
                </ItemWrapper>
              </TableHeader>

              <Divider sx={{ color: color.secondary.dark[300] }} />

              <TableContent>
                {linkedAccounts.filter((la: any) => la.accountId !== account?.accountId).map((la: any, index: number) => (
                  <TableItem key={index}>
                    <ItemWrapper width='20%'>
                      <Typography color={color.grey[700]} fontSize={14}>
                        {la.accountId}
                      </Typography>
                    </ItemWrapper>
                    <ItemWrapper width='80%'>
                      <Typography color={color.grey[700]} fontSize={14}>
                        {la.serviceAddress}
                      </Typography>
                    </ItemWrapper>
                  </TableItem>
                ))}
              </TableContent>
            </Table>
          )}
      </Collapse>
      <LinkAccountDialog
        open={showLinkDialog}
        mainAccountNumber={account?.accountId || ''}
        onClose={() => {
          setShowLinkDialog(!showLinkDialog)
        }}
        refreshLinkedAccounts={initLinkedAccounts}
        linkedAccounts={linkedAccounts}
      />
      <UnlinkAccountDialog
        linkedAccounts={linkedAccounts}
        mainAccountNumber={account?.accountId || ''}
        open={showUnlinkDialog}
        onClose={() => {
          setShowUnlinkDialog(!showUnlinkDialog)
        }}
      />
    </Box>
  )
}

interface ILinkedAccountsTableMobileView {
  accountId: string
  serviceAddress: string
}

const LinkedAccountsTableMobileView = (
  props: ILinkedAccountsTableMobileView
): JSX.Element => {
  return (
    <Box
      display='flex'
      flexDirection='column'
      p={2}
      gap={2}
      borderBottom={`solid 1px ${color.secondary.dark[100]}`}
    >
      <Box display='flex' flexDirection='column'>
        <FormLabel textcolor={color.grey[600]}>Account Number</FormLabel>
        <Typography color={color.grey[700]} fontSize={14}>
          {props?.accountId}
        </Typography>
      </Box>

      <Box display='flex' flexDirection='column'>
        <FormLabel textcolor={color.grey[600]}>Service Address</FormLabel>
        <Typography color={color.grey[700]} fontSize={14}>
          {props?.serviceAddress}
        </Typography>
      </Box>
    </Box>
  )
}

const Table = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  background: color.white
})

const TableHeader = styled(Box)({
  display: 'flex',
  borderBottom: color.secondary.dark[300],
  fontSize: 14,
  fontWeight: 600,
  paddingLeft: 20,
  paddingRight: 20
})

const TableContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: 20,
  paddingRight: 20
})

const TableItem = styled(Box)({
  display: 'flex',
  '&:hover': {
    backgroundColor: color.primary[50]
  }
})

const ItemWrapper = styled(Box)<{ width?: string }>(({ width }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  wordWrap: 'break-word',
  wordBreak: 'break-all',
  width,
  paddingTop: 10,
  paddingBottom: 10,
  color: color.black,
  fontSize: 14,
  fontWeight: 400
}))
